/**
 * contactCsvExport.ts
 * Generates RFC 4180 compliant CSV files with UTF-8 BOM for full Microsoft Excel compatibility.
 */

import { Contact } from '@/services/contactsApi'

function escapeCsvField(field: unknown): string {
  if (field === null || field === undefined) return '""'
  const stringValue = String(field)
  // If field contains quotes, commas or line breaks, enclose in quotes and escape quotes
  if (/[",\n\r]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`
  }
  return `"${stringValue}"`
}

export function generateContactsCsvContent(contacts: Contact[]): string {
  const headers = [
    'Nombre',
    'WhatsApp ID',
    'Teléfono',
    'Email',
    'Empresa',
    'Cargo',
    'Estado',
    'Favorito',
    'Canal',
    'Etiquetas',
    'Total Conversaciones',
    'Última Interacción',
  ]

  const rows = contacts.map((c) => {
    const tagsString = (c.tags || []).map((t) => t.name).join(', ')
    const lastContactStr = c.lastContactAt
      ? new Date(c.lastContactAt).toISOString().split('T')[0]
      : 'Sin registro'

    return [
      escapeCsvField(c.name || ''),
      escapeCsvField(c.waContact || ''),
      escapeCsvField(c.phone || ''),
      escapeCsvField(c.email || ''),
      escapeCsvField(c.company || ''),
      escapeCsvField(c.position || ''),
      escapeCsvField(c.status || 'active'),
      escapeCsvField(c.isFavorite ? 'Sí' : 'No'),
      escapeCsvField(c.source || 'whatsapp'),
      escapeCsvField(tagsString),
      escapeCsvField(c.totalConversations ?? 0),
      escapeCsvField(lastContactStr),
    ].join(',')
  })

  // Prepend UTF-8 BOM (\uFEFF) for Excel compatibility
  return '\uFEFF' + [headers.map(escapeCsvField).join(','), ...rows].join('\r\n')
}

export function downloadContactsCsv(contacts: Contact[], filename = 'contactos.csv'): boolean {
  if (!contacts || contacts.length === 0) return false

  const csvContent = generateContactsCsvContent(contacts)
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })

  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)

  return true
}
